<section class="users-page">
  <!-- Title + actions row -->
  <div class="d-flex align-items-center justify-content-between mb-3">
    <div>
      <h3 class="title mb-1">User Management</h3>
      <p class="subtitle page-desc mb-0">
        Manage, moderate, and view user profiles.
      </p>
    </div>
  </div>

  <!-- Search row -->
  <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-3">
    <div class="d-flex align-items-center gap-2">
      <app-search-box tooltipText="Search By firstName, lastName, and email" [value]="searchTerm()" (search)="onSearch($event)" />
      @if (hasSelection()) {
        <button class="btn btn-icon btn-delete-bulk tooltip-wrap" (click)="deleteSelectedUsers()">
          <i class="pi pi-trash" style="font-size: 1.25rem;"></i>
          <span class="tooltip-box">Delete Selected</span>
        </button>
      }
    </div>

    <div class="d-flex align-items-center gap-3 ms-auto">
      <div class="d-flex align-items-center gap-2 border rounded px-1 header-icon-actions-wrapper">
        <button type="button" class="btn btn-icon p-1 header-icon-action-btn" (click)="fetchUsers()" title="Refresh">
          <i class="pi pi-refresh" style="font-size: 1.25rem; color: #6b7280;"></i>
        </button>
        <button type="button" class="btn btn-icon p-1 tooltip-wrap header-icon-action-btn"
          (click)="toggleIncludeDeleted()">
          <i class="pi pi-asterisk" style="font-size: 1.25rem;"
          [style.color]="includeDeleted() ? '#ec4899' : '#6b7280'"></i>
          <span class="tooltip-box">Include Mark Deleted</span>
        </button>
      </div>
    </div>
  </div>

  <div class="table-container shadow">
    <table class="user-table">
      <thead>
        <tr>
          <th class="checkbox-col" style="width: 48px; background-color: #f8f9fa;">
            <input type="checkbox" [checked]="allSelected()" (change)="toggleSelectAll()" />
          </th>
          <th class="sortable" (click)="onSort('firstName')">
            User <span class="sort-indicator" [class.active]="currentSortField() === 'firstName'">{{ getSortIcon('firstName') }}</span>
          </th>
          <th class="sortable" (click)="onSort('email')">
            Email <span class="sort-indicator" [class.active]="currentSortField() === 'email'">{{ getSortIcon('email') }}</span>
          </th>
          <th class="sortable text-center" (click)="onSort('phoneNumber')">
            Phone <span class="sort-indicator" [class.active]="currentSortField() === 'phoneNumber'">{{ getSortIcon('phoneNumber') }}</span>
          </th>
        
          <th class="sortable text-center" (click)="onSort('lastLoginAt')">
            Last Active <span class="sort-indicator" [class.active]="currentSortField() === 'lastLoginAt'">{{ getSortIcon('lastLoginAt') }}</span>
          </th>
          <th class="sortable text-center" (click)="onSort('status')">
            Status <span class="sort-indicator" [class.active]="currentSortField() === 'status'">{{ getSortIcon('status') }}</span>
          </th>
          <th class="text-center">Action</th>
        </tr>
      </thead>
      <tbody>
        @if (this.loading()) {
          <tr>
            <td colspan="100%">
              <app-spinner-loader [width]="35" />
            </td>
          </tr>
        } @else if (this.users().length === 0) {
          <tr>
            <td colspan="100%" class="no-data-cell">No user found</td>
          </tr>
        } @else {
          @for (u of users(); track trackById($index, u)) {
            <tr [ngClass]="{'deleted-row': !!u.deletedAt}"
              [class.selected]="u.selected">
              <td class="checkbox-col">
                <input type="checkbox" [(ngModel)]="u.selected" (change)="updateSelectAllState()" [disabled]="!!u.deletedAt" />
              </td>
              <td>
                <div class="d-flex align-items-center gap-2">
                  <div class="avatar position-relative" [class.avatar-img-wrap]="getProfileImage(u)"
                    [style.cursor]="getProfileImage(u) ? 'pointer' : 'default'"
                    (click)="getProfileImage(u) && openPreview(u)">
                    @if (getProfileImage(u)) {
                      <img [src]="getProfileImage(u)" alt="Profile" class="avatar-photo" />
                    }
                    @if (!getProfileImage(u)) {
                      <span>
                        {{ getInitials(u) }}
                      </span>
                    }
                    @if (u.deletedAt) {
                      <span class="deleted-dot" title="Deleted User"></span>
                    }
                  </div>
                  <span class="fw-semibold">{{ getDisplayName(u) | titlecase }}</span>
                </div>
              </td>
              <td class="text-muted">{{ u.email || 'N/A'}}</td>
              <td class="text-center">
                {{ u.phoneNumber ? u.phoneNumber : "N/A" }}
              </td>
              
              <td class="text-center">
                {{ u.lastLoginAt ? (u.lastLoginAt | date:'dd-MM-yyyy') : 'N/A' }}
              </td>
              <td class="text-center">
                @if (!u.deletedAt) {
                  <label class="switch">
                    <input type="checkbox" [checked]="u.isActive" (click)="openStatusModal($event, u)" />
                    <span class="slider round"></span>
                  </label>
                }
                @if (u.deletedAt) {
                  <span class="badge bg-danger">Deleted</span>
                }
              </td>
              <td class="text-center">
                <div class="d-flex justify-content-center">
                  <button class="btn btn-view" (click)="view(u)">View</button>
                </div>
              </td>
            </tr>
          }
        }
      </tbody>
    </table>

    <div class="pagination-section">
      <div class="pagination-info">
        <span>
          Showing {{ startIndex }} to {{ endIndex }} of
          {{ totalCount() }} Entries
        </span>
      </div>
      <div class="d-flex gap-2">
        @if (users().length !== 0) {
          <label class="page-size-label" for="pageSizeSelect">Page Size:</label>
          <select id="pageSizeSelect" [(ngModel)]="pageSize" (ngModelChange)="onPageSizeChange($event)"
            class="form-select">
            @for (size of pageSizes; track size) {
              <option [value]="size">
                {{ size }}
              </option>
            }
          </select>
        }
        <button class="btn btn-outline pagination-btn"
          [disabled]="currentPage() === 1 || loading() || users().length === 0" (click)="previousPage()">
          &lt; Prev
        </button>
        <button class="btn btn-outline pagination-btn" [disabled]="currentPage() === totalPages || loading()"
          (click)="nextPage()">
          Next &gt;
        </button>
      </div>
    </div>
  </div>
</section>

@if (isView() && selectedUser()) {
  <app-user-details-modal [visible]="isView()" (onClose)="closeView()" [user]="selectedUser()!"></app-user-details-modal>
}

@if (isStatusModalVisible() && selectedUserForStatus()) {
  <app-user-status-modal [visible]="isStatusModalVisible()" (onClose)="closeStatusModal()"
  [user]="selectedUserForStatus()!" (onAction)="handleStatusAction($event)"></app-user-status-modal>
}



@if (previewPopupUrl()) {
  <div class="img-preview-backdrop" (click)="closePreview()">
    <div class="img-preview-box d-flex align-items-center gap-2" (click)="$event.stopPropagation()"
      style="background: transparent; box-shadow: none; border: none; max-width: 600px;">
      @if (previewPhotos().length > 1) {
        <button type="button"
          class="btn btn-outline-light rounded-circle p-2 d-flex align-items-center justify-content-center"
          (click)="prevPreviewPhoto($event)"
          style="border: none; background: rgba(0,0,0,0.5); color: white; width: 40px; height: 40px; min-width: 40px; font-size: 16px; cursor: pointer;">
          <i class="fa-solid fa-chevron-left"></i>
        </button>
      }
      <div class="position-relative"
        style="background: #fff; border-radius: 12px; padding: 8px; box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4); flex: 1;">
        <button class="img-preview-close" (click)="closePreview()">
          <i class="fa-solid fa-xmark"></i>
        </button>
        <img [src]="previewPopupUrl()!" alt="Preview" class="img-preview-full"
          style="cursor: pointer; width: 100%; border-radius: 8px;" (click)="openImageInNewTab(previewPopupUrl()!)"
          title="Click to view in new tab" />
        </div>
        @if (previewPhotos().length > 1) {
          <button type="button"
            class="btn btn-outline-light rounded-circle p-2 d-flex align-items-center justify-content-center"
            (click)="nextPreviewPhoto($event)"
            style="border: none; background: rgba(0,0,0,0.5); color: white; width: 40px; height: 40px; min-width: 40px; font-size: 16px; cursor: pointer;">
            <i class="fa-solid fa-chevron-right"></i>
          </button>
        }
      </div>
    </div>
  }